import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { useQuery } from '@tanstack/react-query';
import { getPublicUserDoc } from '@util/firestore/users';
import { cn, getCountryLabelFromCode } from '@util/index';
import { navigateToProductInfoTab } from '@util/productInfoTabNavigation';
import type { ProductDocument } from 'models/product';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
export default function ProductConditions({
  product,
}: {
  product: ProductDocument;
}) {
  const sellerNote = product.specs?.['Seller Notes'] as string | undefined;
  const [isSellerNoteExpanded, setIsSellerNoteExpanded] = useState(false);
  const [hasMoreThanTwoLines, setHasMoreThanTwoLines] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  const { data: publicSeller } = useQuery({
    queryKey: ['publicUser', product.seller_id],
    queryFn: async () => {
      const seller = await getPublicUserDoc({ uid: product.seller_id });
      return seller;
    },
    enabled: !!product.seller_id,
  });
  useEffect(() => {
    if (!textRef.current || !sellerNote) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasMoreThanTwoLines(false);
      return;
    }

    const element = textRef.current;
    const originalClasses = element.className;
    element.classList.remove('line-clamp-2', 'max-h-[8rem]');
    const fullHeight = element.scrollHeight;
    element.className = originalClasses;

    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
    const maxHeight = lineHeight * 2;
    setHasMoreThanTwoLines(fullHeight > maxHeight);
  }, [sellerNote]);

  const shipFromCountry =
    product.shipment?.ship_from.country_code ?? publicSeller?.country?.code;
  const isInternational = shipFromCountry && shipFromCountry !== 'US';
  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex flex-col gap-1">
        {isInternational && (
          <Item
            title="Ships From"
            value={getCountryLabelFromCode(shipFromCountry)}
          />
        )}
        <Item
          title="Condition"
          value={product.condition}
          sellerNote={sellerNote}
        />

        {sellerNote && (
          <div className="flex flex-col gap-1">
            <p
              ref={textRef}
              className={cn(
                'text-[1.5rem] italic text-zinc-800',
                !isSellerNoteExpanded && 'line-clamp-2 max-h-[8rem]'
              )}
            >
              {sellerNote}
            </p>

            {hasMoreThanTwoLines && (
              <button
                type="button"
                onClick={() => setIsSellerNoteExpanded(!isSellerNoteExpanded)}
                className="w-fit text-[1.5rem] font-medium text-brand-secondary"
              >
                {isSellerNoteExpanded ? 'Show Less' : 'Read More'}
              </button>
            )}
          </div>
        )}
      </div>

      {!!product.fitting_bikes?.bikes?.length && (
        <Item
          title="Fitment"
          linkHref="#fitment"
          linkText="See fitment table"
          linkClassName="text-[1.5rem] font-medium text-brand-secondary underline"
        />
      )}

      {['Casual', 'Gear'].some((x) =>
        product.category.toLowerCase().includes(x.toLowerCase())
      ) &&
        !product?.variations &&
        (product.size?.letter || !!product.size?.number) && (
          <Item
            title="Size"
            value={`${product.gender === 'Y' ? 'Youth ' : ''} ${
              product.size?.letter
                ? product.size?.letter
                : product.size?.number
                ? product.size?.number.toString()
                : ''
            }`}
          />
        )}

      {product.category2 === 'Tires' && product.size?.letter && (
        <Item
          title={`${product.size.letter1 ? 'Front ' : ''}Tire Size`}
          value={product.size.letter}
        />
      )}

      {product.category2 === 'Tires' && product.size?.letter1 && (
        <Item title="Rear Tire Size" value={product.size.letter1} />
      )}
    </div>
  );
}

const Item = ({
  title,
  value,
  linkText,
  linkHref,
  sellerNote,
  linkClassName,
}: {
  title: string;
  value?: string;
  linkText?: string;
  linkHref?: string;
  linkClassName?: string;
  sellerNote?: string;
}) => {
  return (
    <div className="flex items-center gap-x-2">
      <span className="text-[1.5rem] font-semibold text-brand-black">
        {title}:
      </span>

      {linkHref ? (
        linkHref.startsWith('#') ? (
          <a
            href={linkHref}
            className={cn(
              'text-[1.5rem] text-brand-black underline',
              linkClassName
            )}
            onClick={(e) => {
              e.preventDefault();
              navigateToProductInfoTab(linkHref.slice(1));
            }}
          >
            {linkText}
          </a>
        ) : (
          <Link
            href={linkHref}
            className={cn(
              'text-[1.5rem] text-brand-black underline',
              linkClassName
            )}
          >
            {linkText}
          </Link>
        )
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-[1.5rem] text-brand-black">{value}</span>

          {sellerNote && (
            <InformationCircleIcon className="h-6 w-6 text-brand-secondary" />
          )}
        </div>
      )}
    </div>
  );
};
